import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, RefreshCw } from 'lucide-react';
import { listQuizSubmissions, downloadQuizSubmissionsCsv } from '../lib/api';

const VARIANT_LABEL = {
  'weight-loss': 'Weight Loss',
};

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function AdminSubmissions() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState(new Set());

  const load = () => {
    if (!localStorage.getItem('alfa_admin_token')) {
      window.location.replace('/admin');
      return;
    }
    setRows(null);
    setError(null);
    listQuizSubmissions(500)
      .then(setRows)
      .catch((e) => {
        if (e.message?.includes('401')) {
          localStorage.removeItem('alfa_admin_token');
          window.location.replace('/admin');
          return;
        }
        setError(e.message || 'Failed to load');
      });
  };

  useEffect(load, []);

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const filtered = rows
    ? (filter === 'all' ? rows : rows.filter((r) => r.variant === filter))
    : [];

  return (
    <div className="min-h-screen relative">
      <div className="telegra-bg" aria-hidden="true" />

      <header className="fixed top-0 left-0 right-0 z-40 bg-[#fbf5ee]/80 backdrop-blur-md border-b border-neutral-900/5">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <Link to="/" data-testid="admin-back-home" className="inline-flex items-center gap-2 text-[14.5px] uppercase tracking-[0.16em] font-semibold text-neutral-950 hover:text-neutral-950">
            <ArrowLeft size={16} /> Home
          </Link>
          <span className="font-display uppercase text-[18px] sm:text-[24px] tracking-tight text-neutral-950">
            Quiz Submissions
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-1.5 bg-white/70 hover:bg-white text-neutral-900 uppercase text-[13px] tracking-[0.14em] font-semibold px-3 py-2 rounded-sm border border-neutral-300"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              onClick={downloadQuizSubmissionsCsv}
              data-testid="admin-export-csv"
              className="inline-flex items-center gap-1.5 bg-neutral-900 hover:bg-neutral-800 text-white uppercase text-[13px] tracking-[0.14em] font-semibold px-3 py-2 rounded-sm"
            >
              <Download size={14} /> Export CSV
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-4 sm:px-6 pt-[88px] sm:pt-[96px] pb-8">
        {/* Filter chips */}
        <div className="flex flex-wrap gap-2 mb-6">
          {['all', 'weight-loss'].map((v) => {
            const count = rows ? (v === 'all' ? rows.length : rows.filter((r) => r.variant === v).length) : 0;
            const active = filter === v;
            return (
              <button
                key={v}
                onClick={() => setFilter(v)}
                data-testid={`admin-filter-${v}`}
                className={`rounded-full px-4 py-2 text-[13.5px] font-medium border transition-colors ${
                  active
                    ? 'bg-neutral-900 text-white border-neutral-900'
                    : 'bg-white/70 text-neutral-950 border-neutral-200 hover:bg-white'
                }`}
              >
                {v === 'all' ? 'All' : VARIANT_LABEL[v]}
                <span className={`ml-2 text-[12.5px] ${active ? 'text-neutral-300' : 'text-neutral-400'}`}>{count}</span>
              </button>
            );
          })}
        </div>

        {error && (
          <div data-testid="admin-error" className="rounded-md border border-red-300 bg-red-50 text-red-800 p-4 mb-6 text-[14.5px]">
            {error}
          </div>
        )}

        {rows === null && !error && (
          <p data-testid="admin-loading" className="uppercase text-[12.5px] tracking-[0.28em] text-neutral-950">Loading…</p>
        )}

        {rows !== null && filtered.length === 0 && (
          <div data-testid="admin-empty" className="rounded-md border border-neutral-300 bg-white/70 p-10 text-center">
            <p className="uppercase text-[12.5px] tracking-[0.24em] text-neutral-950">No submissions yet</p>
          </div>
        )}

        {filtered.length > 0 && (
          <div data-testid="admin-submissions-table" className="rounded-lg border border-neutral-900/10 bg-white/80 backdrop-blur overflow-hidden">
            <table className="w-full text-left">
              <thead className="bg-neutral-50 border-b border-neutral-200 text-[12.5px] uppercase tracking-[0.14em] text-neutral-950">
                <tr>
                  <th className="py-3 px-4 font-semibold">Submitted</th>
                  <th className="py-3 px-4 font-semibold">Variant</th>
                  <th className="py-3 px-4 font-semibold">Name</th>
                  <th className="py-3 px-4 font-semibold">Email</th>
                  <th className="py-3 px-4 font-semibold">Answers</th>
                </tr>
              </thead>
              <tbody className="text-[15px] text-neutral-950">
                {filtered.map((r) => {
                  const isOpen = expanded.has(r.id);
                  return (
                    <React.Fragment key={r.id}>
                      <tr className="border-b border-neutral-100 hover:bg-neutral-50/50">
                        <td className="py-3 px-4 text-neutral-950 whitespace-nowrap">{formatDate(r.created_at)}</td>
                        <td className="py-3 px-4">
                          <span className="inline-block rounded-full bg-neutral-100 border border-neutral-200 px-2.5 py-0.5 text-[12.5px] uppercase tracking-[0.12em] font-semibold text-neutral-950">
                            {VARIANT_LABEL[r.variant] || r.variant}
                          </span>
                        </td>
                        <td className="py-3 px-4 font-medium">{r.name}</td>
                        <td className="py-3 px-4"><a className="text-neutral-950 hover:text-neutral-950" href={`mailto:${r.email}`}>{r.email}</a></td>
                        <td className="py-3 px-4">
                          <button
                            onClick={() => toggle(r.id)}
                            data-testid={`admin-toggle-answers-${r.id}`}
                            className="text-[13.5px] uppercase tracking-[0.14em] font-semibold text-neutral-950 hover:text-neutral-950"
                          >
                            {isOpen ? 'Hide' : 'View'}
                          </button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-neutral-50 border-b border-neutral-100">
                          <td colSpan={5} className="px-4 py-4">
                            <pre className="whitespace-pre-wrap break-words text-[14px] text-neutral-950 font-mono">
{JSON.stringify(r.answers, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
